export const MAX_INVOICE_BYTES = 20 * 1024 * 1024;
export const INVOICE_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/webp"];

export function invoiceErrorMessage(error) {
  const code = error?.code || "";
  if (["storage/unauthenticated", "auth/user-token-expired", "auth/invalid-user-token", "invoice/session-changed"].includes(code)) return "Your employee sign-in expired or changed. Sign in again before accessing invoices.";
  if (code === "invoice/claims-required") return "Your employee account is missing active file-access permissions. An administrator must synchronize your Authentication role and active status; refreshing alone will not fix missing permissions.";
  if (code === "storage/unauthorized") return "Invoice access was denied. Carrier bills require an active admin or owner account. Ask an administrator to check Authentication claims and the Storage policy; the document was not made public.";
  if (["storage/retry-limit-exceeded", "storage/unknown", "auth/network-request-failed"].includes(code)) return "The invoice service could not complete the request. Check your connection and retry. If it continues, ask an administrator to inspect the diagnostic error.";
  if (["storage/bucket-not-found", "storage/project-not-found", "storage/no-default-bucket"].includes(code)) return "Invoice Storage is not configured correctly. Contact an administrator to check the project and bucket.";
  if (code === "storage/object-not-found") return "The attached invoice file could not be found. Ask an administrator to review this record.";
  if (["portal/app-check-required", "portal/app-check-failed"].includes(code)) return "Invoice browser verification failed or is not configured. Reload and allow site verification requests, or contact an administrator.";
  if (code === "storage/quota-exceeded") return "Invoice Storage has reached its quota. Contact an administrator.";
  return error?.message || "The invoice request failed. Contact an administrator.";
}

export async function prepareInvoiceSession(auth, kind, expectedActorId) {
  if (!["po", "carrier"].includes(kind)) throw new Error("Unsupported invoice document type.");
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) throw Object.assign(new Error("Sign in before accessing invoices."), { code: "storage/unauthenticated" });
  const { claims } = await user.getIdTokenResult(true);
  if (auth.currentUser?.uid !== user.uid || expectedActorId && expectedActorId !== user.uid) {
    throw Object.assign(new Error("Employee sign-in changed."), { code: "invoice/session-changed" });
  }
  const roles = kind === "carrier" ? ["admin", "owner"] : ["admin", "owner", "staff"];
  if (claims.active !== true || !roles.includes(claims.role)) {
    throw Object.assign(new Error("Active invoice-access Authentication claims are required."), { code: "invoice/claims-required" });
  }
  return user.uid;
}

export function validateInvoiceFile(file, pdfOnly = false) {
  if (!file || !Number.isInteger(file.size) || file.size <= 0 || file.size > MAX_INVOICE_BYTES) {
    throw new Error("Choose a nonempty invoice document up to 20 MB.");
  }
  if (pdfOnly ? file.type !== "application/pdf" : !INVOICE_TYPES.includes(file.type)) {
    throw new Error(pdfOnly ? "Carrier statements require a PDF." : "Invoices must be PDF, PNG, JPEG or WebP.");
  }
}

export function hasInvoice(record) {
  const d = record?.invoiceDocument;
  return Boolean(record?.invoiceNumber?.trim() && d && INVOICE_TYPES.includes(d.contentType)
    && d.size > 0 && d.size <= MAX_INVOICE_BYTES && /^[a-f0-9]{64}$/.test(d.sha256 || "")
    && /^spend-documents\/(po|carrier)\/[^/]+\/[^/]+\/invoice$/.test(d.path || ""));
}

export function purchaseOrderNumber(id, date = new Date()) {
  if (!/^[A-Za-z0-9]{20}$/.test(id)) throw new Error("Invalid purchase order identifier.");
  const day = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}`;
  return `PO-${day}-${id}`;
}
